import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { membersAPI } from '../../../services/api';
import HelpInvitePlayerPanel from './HelpInvitePlayerPanel';

const Box = styled.div`
  margin-top: 0.75rem;
  padding: 1rem 1.1rem;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
`;

const MemberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid #f1f5f9;
  &:last-child {
    border-bottom: none;
  }
`;

const Muted = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.875rem;
  color: #64748b;
  line-height: 1.45;
`;

const RemoveBtn = styled.button`
  padding: 0.35rem 0.65rem;
  font-size: 0.78rem;
  font-weight: 600;
  border-radius: 8px;
  border: 1px solid rgba(213, 43, 30, 0.45);
  background: rgba(213, 43, 30, 0.08);
  color: #b91c1c;
  cursor: pointer;
`;

const Field = styled.label`
  display: block;
  margin-top: 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  display: block;
  width: 100%;
  margin-top: 0.35rem;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 0.95rem;
  box-sizing: border-box;
`;

const TextArea = styled.textarea`
  display: block;
  width: 100%;
  margin-top: 0.35rem;
  min-height: 72px;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 0.95rem;
  resize: vertical;
  box-sizing: border-box;
`;

const ConfirmBtn = styled.button`
  margin-top: 0.75rem;
  padding: 0.6rem 1rem;
  border: none;
  border-radius: 8px;
  background: #b91c1c;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PrimaryBtn = styled.button`
  margin-top: 0.75rem;
  padding: 0.6rem 1rem;
  border: none;
  border-radius: 8px;
  background: #043663;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SectionTitle = styled.h3`
  margin: 1.25rem 0 0.35rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #043663;
`;

export default function HelpRosterPanel({ teamContext }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const teamId = teamContext?.teamId;
  const seasonId = teamContext?.seasonId;
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removePassword, setRemovePassword] = useState('');
  const [removeReason, setRemoveReason] = useState('');
  const [slotOpened, setSlotOpened] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [requestSent, setRequestSent] = useState(false);

  const { data, isLoading } = useQuery(
    ['helpRosterMembers', teamId, seasonId],
    () => membersAPI.getMembers(teamId, seasonId),
    { enabled: !!teamId && !!seasonId }
  );

  const members = data?.data?.members || [];
  const viewerIsCaptain = data?.data?.viewerIsCaptain === true;
  const viewerMember = members.find((m) => m.is_self);

  const removeMutation = useMutation(
    ({ memberId, body }) => membersAPI.removeMember(memberId, body, teamId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['helpRosterMembers', teamId, seasonId]);
        queryClient.invalidateQueries(['playerTeams']);
        toast.success(t('teamDashboardPage.toastMemberRemoved'));
        setRemoveTarget(null);
        setRemovePassword('');
        setRemoveReason('');
        setSlotOpened(true);
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || err.message || t('teamDashboardPage.toastMemberRemoveFailed'));
      },
    }
  );

  const requestRemovalMutation = useMutation(
    (body) => membersAPI.requestSelfRemoval(body, teamId),
    {
      onSuccess: () => {
        toast.success(t('phase2.playerHelpWizard.roster.requestSent'));
        setRequestSent(true);
        setRequestMessage('');
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || t('phase2.playerHelpWizard.roster.requestFailed'));
      },
    }
  );

  if (isLoading) {
    return <Muted>{t('teamDashboardPage.loadingTeamMembers')}</Muted>;
  }

  if (!viewerIsCaptain) {
    return (
      <Box>
        <Muted>{t('phase2.playerHelpWizard.roster.memberHint')}</Muted>
        {requestSent ? (
          <Muted>{t('phase2.playerHelpWizard.roster.requestSentDetail')}</Muted>
        ) : (
          <>
            <Field>
              {t('phase2.playerHelpWizard.roster.requestMessageLabel')}
              <TextArea
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                placeholder={t('phase2.playerHelpWizard.roster.requestMessagePlaceholder')}
              />
            </Field>
            <PrimaryBtn
              type="button"
              disabled={requestRemovalMutation.isLoading}
              onClick={() => requestRemovalMutation.mutate({ message: requestMessage.trim() })}
            >
              {requestRemovalMutation.isLoading
                ? t('phase2.playerHelpWizard.roster.requestSending')
                : t('phase2.playerHelpWizard.roster.requestRemoval')}
            </PrimaryBtn>
          </>
        )}
      </Box>
    );
  }

  return (
    <>
      <Box>
        <Muted>{t('phase2.playerHelpWizard.roster.captainHint')}</Muted>
        {members.length === 0 ? (
          <Muted>{t('teamDashboardPage.emptyMembersBody')}</Muted>
        ) : (
          members.map((m) => {
            const name = [m.first_name, m.last_name].filter(Boolean).join(' ').trim() || '—';
            const roleLabel =
              m.position === 'captain' ? t('teamDashboardPage.roleCaptain') : t('teamDashboardPage.roleMember');
            return (
              <MemberRow key={m.id}>
                <span>
                  {name} ({roleLabel})
                </span>
                {!m.is_self && m.position !== 'captain' ? (
                  <RemoveBtn type="button" onClick={() => setRemoveTarget(m)}>
                    {t('teamDashboardPage.removeButton')}
                  </RemoveBtn>
                ) : null}
              </MemberRow>
            );
          })
        )}

        {removeTarget ? (
          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
            <Muted>
              {t('teamDashboardPage.removeTeammateBody', {
                name: `${removeTarget.first_name} ${removeTarget.last_name}`,
              })}
            </Muted>
            <Field>
              {t('teamDashboardPage.removeLoginPasswordLabel')}
              <Input
                type="password"
                autoComplete="current-password"
                value={removePassword}
                onChange={(e) => setRemovePassword(e.target.value)}
              />
            </Field>
            <Field>
              {t('teamDashboardPage.removeReasonLabel')}
              <TextArea
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
                placeholder={t('teamDashboardPage.placeholderRemoveReason')}
              />
            </Field>
            <ConfirmBtn
              type="button"
              disabled={
                removeMutation.isLoading ||
                removePassword.length < 1 ||
                removeReason.trim().length < 5
              }
              onClick={() =>
                removeMutation.mutate({
                  memberId: removeTarget.id,
                  body: { password: removePassword, reason: removeReason.trim() },
                })
              }
            >
              {removeMutation.isLoading ? t('teamDashboardPage.removing') : t('teamDashboardPage.removeMember')}
            </ConfirmBtn>
          </div>
        ) : null}
      </Box>

      {(slotOpened || (teamContext?.rosterRow?.activeMemberCount ?? members.length) < 2) ? (
        <>
          <SectionTitle>{t('phase2.playerHelpWizard.roster.inviteReplacementTitle')}</SectionTitle>
          <HelpInvitePlayerPanel teamContext={teamContext} />
        </>
      ) : null}
    </>
  );
}
